import { useQuery } from '@apollo/client';
import { Container, LoadingOrError } from '@custom/dx3';
import { Typography } from '@mui/material';
import classnames from 'classnames';
import React, { useMemo } from 'react';
import { COPY as ALL_COPY } from '../../assets/copy/copy';
import { CheckmarkCheckedIcon } from '../../assets/svgs';
import { ArticleDisplay } from '../../components/ArticleDisplay/ArticleDisplay';
import { LatestBadges } from '../../components/LatestBadges/LatestBadges';
import { LevelDisplay } from '../../components/LevelDisplay/LevelDisplay';
import ROUTES from '../../routes/routes';
import { GET_DASHBOARD_DATA } from '../../store/queries/containerQueries';
import { ISODateToDate } from '../../utils/formatters';
import styles from './DashboardSectionStyles.module.scss';

const COPY = ALL_COPY.HOME_SCREEN.MY_DASHBOARD;

function DashboardSection(): JSX.Element {
  const { data, loading } = useQuery(GET_DASHBOARD_DATA, {
    fetchPolicy: 'cache-and-network',
  });

  const latestBadges = useMemo(() => {
    return data?.me?.badges
      ? data.me.badges.slice(0, 3).map((badge) => {
          const lastEarnedTime = ISODateToDate(badge.lastEarnedTime);
          return {
            imageUrl: badge.thumbUrl,
            title: badge.title,
            lastEarnedTime,
          };
        })
      : [];
  }, [data?.me?.badges]);

  const articles = useMemo(() => {
    return (
      data?.me?.articles?.items?.map((article) => {
        return {
          id: article.id,
          summary: article.subtitle,
          title: article.title,
          iconUrl: article.thumbnailImageUrl,
          iconAltText: article.thumbnailImageAltText,
          url:
            article.contentURL ||
            ROUTES.ARTICLES.replace(
              ':id',
              article?.id?.toString() ?? '' // Note: this will never happen due to the filter above.
            ),
        };
      }) ?? []
    );
  }, [data]);

  return (
    <Container className={classnames(styles['dashboard-container'])}>
      <LoadingOrError loading={loading} error={false}>
        <div className={classnames(styles['user-info'], 'shadowed-paper')}>
          <LevelDisplay
            className="level-display"
            currentLevel={data?.me?.level?.currentLevel}
            currentPoints={data?.me?.level?.currentPoints}
            nextLevel={data?.me?.level?.nextLevel}
            nextLevelPoints={data?.me?.level?.nextLevelPoints}
            progressPercentage={data?.me?.level?.progressPercentage ?? 0}
          />
          <LatestBadges
            badges={latestBadges}
            externalBadgeWallLink={data?.me?.badgesLink}
          />
        </div>
        <div
          className={classnames(styles['articles-content'], 'shadowed-paper')}
        >
          <p className={styles['title']}>{COPY.COMMUNICATIONS}</p>

          {articles.length == 0 ? (
            <div className={styles['no-articles']}>
              <CheckmarkCheckedIcon />
              <Typography variant="h5"> {COPY.NO_ARTICLES_HEADER} </Typography>
              <Typography> {COPY.NO_ARTICLES_SUBHEADING} </Typography>
            </div>
          ) : (
            articles
              .filter((article) => article.id)
              .map((article) => {
                return (
                  <ArticleDisplay
                    key={article.id}
                    summary={article.summary}
                    url={article.url}
                    title={article.title}
                    iconUrl={article.iconUrl}
                    iconAltText={article.iconAltText}
                  />
                );
              })
          )}
        </div>
      </LoadingOrError>
    </Container>
  );
}

export default DashboardSection;
